import { canvasUtils, Color, type ColorSource, type Renderer, RendererType, type Texture } from "pixi.js";

/**
 * Pixi's Canvas 2D renderer (what draws the game when the browser has no WebGL, as with graphics acceleration off)
 * tints a picture by drawing a tinted copy of it on a canvas of its own, kept on the picture for good, one for every
 * colour it was ever drawn in (`canvasUtils.getTintedCanvas`, the picture's `tintCache`). The atlas's pictures live as
 * long as the page, and a tint that changes all the time (the players' bodies and hands shaded as they move, the bars,
 * the name tags) made a new copy at nearly every frame: in the lab two short battles left 3,100 of them that nothing
 * ever let go, 48 million pixels (some 190 MB), and each new colour was one more canvas drawn (0.96 audit).
 *
 * The colours are now rounded to 32 levels a channel (a step nobody sees), and only the copies used last are kept
 * (`KEPT_TINTS`, and `KEPT_TINT_PIXELS` together): one let go is given back its memory and drawn again if it's needed.
 */

/** a channel's step: 256 / 8 = 32 levels */
export const TINT_STEP = 8;
/** the tinted copies kept, every picture's together, and their pixels together (12 million: some 48 MB) */
export const KEPT_TINTS = 400;
export const KEPT_TINT_PIXELS = 12_000_000;

/** a 0xRRGGBB colour with each channel rounded to `TINT_STEP` (white stays white, black black) */
export const roundTint = (color: number): number => {
    const channel = (shift: number): number => Math.min(255, Math.round(((color >> shift) & 0xff) / TINT_STEP) * TINT_STEP) << shift;
    return (channel(16) | channel(8) | channel(0)) >>> 0;
};

/** what `canvasUtils` has of the tinting (`limitCanvasTints` swaps its `getTintedCanvas`: a test gives its own) */
export interface CanvasTinting {
    getTintedCanvas: (sprite: { texture: Texture }, color: ColorSource) => unknown
}

interface Tinted { width: number, height: number }

/**
 * the Canvas 2D renderer's tinted copies kept to the last `kept` used (and `pixels` together), their colours rounded;
 * nothing for WebGL
 */
export const limitCanvasTints = (renderer: Pick<Renderer, "type">, utils: CanvasTinting = canvasUtils as unknown as CanvasTinting, kept = KEPT_TINTS, pixels = KEPT_TINT_PIXELS): void => {
    if (renderer.type !== RendererType.CANVAS) return;
    const tint = utils.getTintedCanvas.bind(utils);
    // each copy kept, by itself (the newest last), with the picture and the colour it's kept under, and its pixels
    const copies = new Map<Tinted, { readonly texture: Texture, readonly key: string, readonly pixels: number }>();
    let keptPixels = 0;
    utils.getTintedCanvas = (sprite, color) => {
        const rounded = roundTint(typeof color === "number" ? color : Color.shared.setValue(color).toNumber());
        const copy = tint(sprite, rounded) as Tinted;
        const key = Color.shared.setValue(rounded).toHex();
        const was = copies.get(copy);
        if (was) {
            copies.delete(copy);
            keptPixels -= was.pixels;
        }
        const size = copy.width * copy.height;
        copies.set(copy, { texture: sprite.texture, key, pixels: size });
        keptPixels += size;
        // (the one just drawn is never let go: it's the newest)
        while (copies.size > 1 && (copies.size > kept || keptPixels > pixels)) {
            const [oldest, { texture, key: oldKey, pixels: oldPixels }] = copies.entries().next().value as [Tinted, { readonly texture: Texture, readonly key: string, readonly pixels: number }];
            copies.delete(oldest);
            keptPixels -= oldPixels;
            const cache = (texture as unknown as { tintCache?: Record<string, unknown> }).tintCache;
            if (cache?.[oldKey] === oldest) delete cache[oldKey];
            // (its memory given back now: a canvas only lets it go when it's collected)
            oldest.width = 0;
            oldest.height = 0;
        }
        return copy;
    };
};
